import { useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectSelectedItem, selectProjectId, selectItemsForParent } from '@/store/selectors';
import { setSelectedItem } from '../../app/features/project/projectSlice';
import { Video, AudioLines, Image, FileText } from 'lucide-react';
import { useVideoThumbnail } from '../../components/shakeApp/useVideoThumbnail';
import type { NormalizedItem } from '@/components/itemList/types';

const typeIconColors: Record<string, string> = {
  video: 'bg-sky-500',
  audio: 'bg-emerald-400',
  image: 'bg-yellow-500',
  doc: 'bg-orange-600',
};

function TypeBadge({ type }: { type: string }) {
  const Icon = type === 'video' ? Video : type === 'audio' ? AudioLines : type === 'image' ? Image : FileText;
  return (
    <div className={`absolute left-1 top-1 z-1 flex size-6 items-center justify-center rounded-full ${typeIconColors[type] ?? 'bg-gray-400'}`}>
      <Icon className="size-3 text-white" />
    </div>
  );
}

/** Persist selected preview item per project in localStorage */
function savePreviewItemId(itemId: string, projectId: string) {
  try {
    const list: { project: string; previewItemOnShowId: string }[] =
      JSON.parse(localStorage.getItem('previewItemOnShowInProjectsList') || '[]');
    const filtered = list.filter((i) => i.project !== projectId);
    filtered.push({ project: projectId, previewItemOnShowId: itemId });
    localStorage.setItem('previewItemOnShowInProjectsList', JSON.stringify(filtered));
  } catch { /* ignore */ }
}

// ── Preview thumbnail for a single item ────────────────────────────
interface PreviewItemProps {
  item: NormalizedItem;
  isActive: boolean;
  onClick: () => void;
}

function PreviewItem({ item, isActive, onClick }: PreviewItemProps) {
  const generatedThumbnail = useVideoThumbnail(
    item.type === 'video' && !item.thumbnailUrl ? item.signedUrl : null,
    0.5,
  );
  const displayThumbnail = item.thumbnailUrl || generatedThumbnail;

  const fallbackIcon = () => {
    switch (item.type) {
      case 'video': return <Video className="size-8 text-muted-foreground" />;
      case 'audio': return <AudioLines className="size-8 text-white" />;
      case 'image': return <Image className="size-8 text-muted-foreground" />;
      default: return <FileText className="size-8 text-muted-foreground" />;
    }
  };

  return (
    <li
      className={`relative flex size-20 w-[7.5rem] cursor-pointer items-center justify-center overflow-hidden rounded-xl transition-all ${
        isActive ? 'border-2 border-primary shadow-md scale-105' : 'border-2 border-transparent opacity-70 hover:opacity-100 hover:border-border/60'
      }`}
      onClick={onClick}
      title={item.title}
    >
      <TypeBadge type={item.type} />
      {item.type === 'audio' ? (
        <div className="absolute inset-0 flex items-center justify-center bg-blue-600">
          <AudioLines className="size-8 text-white" />
        </div>
      ) : displayThumbnail ? (
        <img
          src={displayThumbnail}
          alt="preview"
          className="absolute inset-0 size-full object-cover transition-transform hover:scale-110"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-background">
          {fallbackIcon()}
        </div>
      )}
    </li>
  );
}

// ── Preview items list ─────────────────────────────────────────────
interface PreviewItemsListProps {
  updateSelectedTask: (task: null) => void;
}

export function PreviewItemsList({ updateSelectedTask }: PreviewItemsListProps) {
  const [showAll, setShowAll] = useState(false);
  const dispatch = useAppDispatch();
  const selectedItem = useAppSelector(selectSelectedItem) as NormalizedItem | null;
  const projectId = useAppSelector(selectProjectId) as string;
  const items = useAppSelector(selectItemsForParent(projectId)) as NormalizedItem[] | undefined;

  const { previewItems, count } = useMemo(() => {
    const list = items?.filter((i) => i.preview) ?? [];
    return { previewItems: showAll ? list : list.slice(0, 12), count: list.length };
  }, [items, showAll]);

  const onItemClick = (item: NormalizedItem) => {
    if (selectedItem?.id !== item.id) {
      dispatch(setSelectedItem(item));
      savePreviewItemId(item.id, projectId);
      updateSelectedTask(null);
    }
  };

  if (!items) {
    return (
      <ul className="flex flex-wrap gap-2">
        {Array.from({ length: 6 }, (_, i) => (
          <li key={i} className="h-20 w-[7.5rem] animate-pulse rounded-xl bg-accent" />
        ))}
      </ul>
    );
  }

  return (
    <ul className="flex flex-wrap gap-2">
      {previewItems.map((item) => (
        <PreviewItem
          key={item.id}
          item={item}
          isActive={item.id === selectedItem?.id}
          onClick={() => onItemClick(item)}
        />
      ))}
      {count > 13 && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="flex h-20 w-[7.5rem] cursor-pointer items-end justify-center pb-2 text-sm font-bold text-info hover:text-info transition-colors"
        >
          Show more
        </button>
      )}
    </ul>
  );
}
